import { DatePipe } from '@angular/common';
import {
  Adjustment,
  CardDetails,
  Dispute,
  ExtendedRetailPaymentStatus,
  PaymentMethodTypeDetails,
  RetailPayment,
  RetailPaymentMethodType,
  RetailPaymentReferenceTypeEnum,
  RetailPaymentStatus,
  RetailPayout,
  RetailRefund,
} from '..';

export enum TransactionReferenceType {
  UNSET,
  INVOICE,
  SALES_ORDER,
  REFUND_INVOICE,
  REFUND_SALES_ORDER,
  DISPUTE,
  REFUND_FAILURE,
  PAYOUT_FAILURE,
}

export function referenceTypeFromPaymentReference(
  rp: RetailPaymentReferenceTypeEnum | undefined,
  isRefund: boolean,
): TransactionReferenceType {
  switch (rp) {
    case RetailPaymentReferenceTypeEnum.RETAIL_PAYMENT_REFERENCE_TYPE_INVOICE:
      if (isRefund) {
        return TransactionReferenceType.REFUND_INVOICE;
      } else {
        return TransactionReferenceType.INVOICE;
      }
    case RetailPaymentReferenceTypeEnum.RETAIL_PAYMENT_REFERENCE_TYPE_SALES_ORDER:
      if (isRefund) {
        return TransactionReferenceType.REFUND_SALES_ORDER;
      } else {
        return TransactionReferenceType.SALES_ORDER;
      }
    default:
      return TransactionReferenceType.UNSET;
  }
}

export class Transaction {
  id?: string;
  customerName?: string;
  amount?: number;
  created?: Date;
  currencyCode?: string;
  customerId?: string;
  description?: string;
  merchantId?: string;
  referenceId?: string;
  referenceType?: TransactionReferenceType;
  status?: ExtendedRetailPaymentStatus;
  failureCode?: string;
  failureMessage?: string;
  applicationFee?: number;
  paymentFacilitatorFee?: number;
  dispute?: Dispute;
  refunds?: RetailRefund[];
  paymentMethodDetailsType?: RetailPaymentMethodType;
  cardDetails?: CardDetails;
  achDetails?: PaymentMethodTypeDetails;
  acssDetails?: PaymentMethodTypeDetails;
  paymentId?: string;
  declineReasonDescription?: string;
  declineReasonNextSteps?: string;

  static fromRetailPayment(customerName: string, rp: RetailPayment): Transaction | null {
    if (!rp) return null;
    return {
      id: rp.id,
      customerName: customerName,
      merchantId: rp.merchantId,
      created: rp.created,
      currencyCode: rp.currencyCode,
      amount: rp.amount,
      referenceId: rp.referenceId,
      referenceType: referenceTypeFromPaymentReference(rp.referenceType, false),
      description: rp.description,
      customerId: rp.customerId,
      status: rp.status || RetailPaymentStatus.Unset,
      failureCode: rp.failureCode,
      failureMessage: rp.failureMessage,
      applicationFee: rp.applicationFee,
      paymentFacilitatorFee: rp.paymentFacilitatorFee || 0,
      dispute: rp.dispute,
      refunds: rp.refunds,
      paymentId: rp.id,
      paymentMethodDetailsType: rp.paymentMethodDetailsType,
      cardDetails: rp.cardDetails,
      achDetails: rp.achDetails,
      acssDetails: rp.acssDetails,
      declineReasonDescription: rp.declineReasonDescription,
      declineReasonNextSteps: rp.declineReasonNextSteps,
    };
  }

  static fromRetailRefund(customerName: string, rr: RetailRefund): Transaction | null {
    if (!rr) return null;
    return {
      id: rr.id,
      customerName: customerName,
      merchantId: rr.payment?.merchantId,
      created: rr.created,
      currencyCode: rr.currency,
      amount: -rr.amount,
      referenceId: rr.payment?.referenceId,
      referenceType: referenceTypeFromPaymentReference(rr.payment?.referenceType, true),
      description: rr.payment?.description,
      customerId: rr.payment?.customerId,
      status: rr.payment?.status,
      failureMessage: rr.failureReason,
      failureCode: '',
      applicationFee: 0,
      paymentFacilitatorFee: 0,
      paymentId: rr.payment?.id,
      cardDetails: rr.payment?.cardDetails,
      paymentMethodDetailsType: rr.payment?.paymentMethodDetailsType,
      achDetails: rr.payment?.achDetails,
      acssDetails: rr.payment?.acssDetails,
    };
  }

  static fromRetailPayout(rp: RetailPayout, datePipe: DatePipe): Transaction | null {
    if (!rp) return null;
    return {
      id: rp.id,
      customerName: '',
      merchantId: '',
      created: rp.arrivalDate,
      currencyCode: rp.currencyCode,
      amount: rp.amount,
      referenceId: rp.id,
      referenceType: TransactionReferenceType.PAYOUT_FAILURE,
      description: datePipe.transform(rp.arrivalDate) || '',
      customerId: '',
      status: RetailPaymentStatus.Failed,
      failureCode: rp.failureCode,
      failureMessage: rp.failureMessage,
      applicationFee: 0,
      paymentFacilitatorFee: 0,
      paymentMethodDetailsType: RetailPaymentMethodType.Unset,
    };
  }

  static fromAdjustment(a: Adjustment): Transaction | null {
    if (!a) return null;
    let status = RetailPaymentStatus.Failed;
    if (a.refund) {
      status = RetailPaymentStatus.Refunded;
    }
    if (a.dispute) {
      status = RetailPaymentStatus.Disputed;
    }
    return {
      amount: a.amount,
      applicationFee: a.fee,
      paymentFacilitatorFee: 0,
      paymentMethodDetailsType: RetailPaymentMethodType.Unset,
      created: a.created,
      currencyCode: a.currency,
      customerId: '',
      customerName: '',
      description: a.description,
      dispute: a.dispute,
      failureCode: '',
      failureMessage: '',
      id: a.dispute ? a.dispute.paymentId : a.id,
      merchantId: '',
      referenceId: '',
      referenceType: a.dispute
        ? TransactionReferenceType.DISPUTE
        : a.refund
          ? TransactionReferenceType.REFUND_FAILURE
          : TransactionReferenceType.UNSET,
      status: status,
      paymentId: a.dispute ? a.dispute.paymentId : a.refund ? a.refund.payment?.id : '',
    };
  }
}
